import { BaseElement } from 'base';
import { bus } from 'bus';
import { ANALYTICS_TOPIC, ANALYTICS_EVENTS } from 'analytics-events';
import { affiliateSignups } from 'affiliate-signups';
import { safeUrl } from 'safe-url';

const BRANCHES = {
  main: ['q1', 'q2', 'q3', 'q4'],
  waitlist: ['q1', 'waitlist']
};

const TIKTOK_HOST = 'tiktok.com';

const TIKTOK_HOST_SUFFIX = '.tiktok.com';

const USERNAME_PATTERN = /^[a-zA-Z0-9_.]{2,24}$/;

const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

class AffiliateQuiz extends BaseElement {
  onConnect() {
    this.card = this.q('[data-quiz-card]');
    this.counter = this.q('[data-quiz-counter]');

    this.screen = 'q1';
    this.branch = 'main';
    const minAge = Number(this.dataset.minAge);
    this.minAge = this.dataset.minAge && Number.isFinite(minAge) ? minAge : 18;
    this.supported = (this.dataset.supportedCountries || '')
      .split(',')
      .map(code => code.trim().toUpperCase())
      .filter(Boolean);
    this.answers = {};
    this.sending = false;
    this.halted = new Set();
    this.attempts = new Map();

    if (!this.dataset.landingUrl) {
      console.error('[affiliate-quiz] landing_url is not set');
    }
    if (!this.dataset.termsUrl) {
      console.error('[affiliate-quiz] terms_url is not set');
    }
    if (!this.dataset.privacyUrl) {
      console.error('[affiliate-quiz] privacy_url is not set');
    }
    if (!this.dataset.sheetEndpoint) {
      console.error('[affiliate-quiz] sheet_endpoint_url is not set');
    }

    this.resetOwnedControls();

    this.listen(document, 'keydown', this.onKeydown);
    this.listen(window, 'pageshow', this.onPageShow);

    this.#wire().catch(error => {
      console.error('[affiliate-quiz] the quiz did not finish wiring:', error);
    });
  }

  async #wire() {
    await customElements.whenDefined('action-button');
    await customElements.whenDefined('select-field');
    await customElements.whenDefined('date-field');
    await customElements.whenDefined('text-field');
    await customElements.whenDefined('selection-field');
    if (!this.isConnected) return;

    this.backButton = this.q('.affiliate-quiz__back');
    this.closeButton = this.q('.affiliate-quiz__close');
    this.select = this.q('select-field');
    this.dateField = this.q('date-field');
    this.emailField = this.q('.affiliate-quiz__email');
    this.usernameField = this.q('.affiliate-quiz__username');

    this.backButton?.run(() => this.goBack());
    this.closeButton?.run(() => this.leave());

    this.#wireCountry();
    this.#wireConsents();
    this.#wireBirthDate();
    this.#wireEmail();
    this.#wireUsername();

    this.cta('q1')?.run(() =>
      this.show(this.branch === 'main' ? 'q2' : 'waitlist')
    );
    this.cta('q2')?.run(() => this.show('q3'));
    this.cta('q3')?.run(() => this.show('q4'));
    this.cta('q4')?.run(() => this.sendHandle());
    this.cta('waitlist')?.run(() => this.sendEmail());
  }

  #wireCountry() {
    if (!this.select) return;
    this.cleanup(
      this.select.onChange(({ value, label }) => {
        this.answers.countryIso = value;
        this.answers.countryName = label;
        this.branch = this.supported.includes(value.toUpperCase())
          ? 'main'
          : 'waitlist';
        if (this.branch === 'main') this.emailField?.clear();
        else this.usernameField?.clear();
        this.syncSubmit();
        this.card.setAttribute('data-branch', this.branch);
        this.updateCounter();
        this.cta('q1')?.setDisabled(false);
      })
    );
  }

  #wireConsents() {
    const terms = this.q('.affiliate-quiz__consent--terms');
    const privacy = this.q('.affiliate-quiz__consent--privacy');
    const california = this.q('.affiliate-quiz__consent--california');

    if (terms) {
      this.cleanup(
        terms.onChange(({ checked }) => {
          this.answers.terms = checked;
          this.syncConsents();
        })
      );
    }
    if (privacy) {
      this.cleanup(
        privacy.onChange(({ checked }) => {
          this.answers.privacy = checked;
          this.syncConsents();
        })
      );
    }
    if (california) {
      this.cleanup(
        california.onChange(({ checked }) => {
          this.answers.california = checked;
        })
      );
    }
  }

  #wireBirthDate() {
    if (!this.dateField) return;
    this.cleanup(
      this.dateField.onChange(({ value }) => {
        if (this.isOldEnough(value)) {
          this.dateField.clearError();
          this.cta('q3')?.setDisabled(false);
        } else {
          this.dateField.setError(this.dataset.ageError);
          this.cta('q3')?.setDisabled(true);
        }
      })
    );
  }

  #wireEmail() {
    if (!this.emailField) return;
    this.listen(this.emailField, 'input', this.onEmailInput);
    this.listen(this.emailField, 'keydown', this.onEmailKeydown);
    this.cleanup(
      this.emailField.onChange(({ value }) => {
        if (value && !EMAIL_PATTERN.test(value)) {
          this.emailField.setError(this.dataset.emailError);
        } else {
          this.emailField.clearError();
        }
      })
    );
  }

  #wireUsername() {
    if (!this.usernameField) return;
    this.listen(this.usernameField, 'input', this.onUsernameInput);
    this.cleanup(
      this.usernameField.onChange(() => {
        if (
          this.usernameTyped() &&
          !USERNAME_PATTERN.test(this.usernameValue())
        ) {
          this.usernameField.setError(this.dataset.usernameError);
        } else {
          this.usernameField.clearError();
        }
      })
    );
  }

  onEmailInput(event) {
    this.syncSubmit();
    const value = event.target.value || '';
    if (!value || EMAIL_PATTERN.test(value)) this.emailField.clearError();
  }

  onEmailKeydown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;
    event.preventDefault();
    this.sendEmail().catch(error => {
      console.error('[affiliate-quiz] the email was not sent:', error);
    });
  }

  onUsernameInput() {
    this.syncSubmit();
    if (!this.usernameTyped() || USERNAME_PATTERN.test(this.usernameValue())) {
      this.usernameField.clearError();
    }
  }

  get countryMissing() {
    return !this.answers.countryIso;
  }

  emailValue() {
    return this.emailField?.control?.value || '';
  }

  usernameTyped() {
    return this.usernameField?.control?.value.trim() || '';
  }

  usernameValue() {
    const typed = this.usernameTyped();
    const withoutProtocol = typed.replace(/^https?:\/\//i, '');
    const segments = withoutProtocol.split(/[/?#]/);
    const candidate = segments[0];
    const firstPathSegment = segments.slice(1).find(Boolean) || '';
    const hostCandidate = candidate.toLowerCase();
    const bareHost = hostCandidate.replace(/\.+$/, '');
    const looksLikeTiktokHost =
      bareHost === TIKTOK_HOST || bareHost.endsWith(TIKTOK_HOST_SUFFIX);
    if (looksLikeTiktokHost && /\.{2,}$/.test(hostCandidate)) {
      return '';
    }
    const singleDotHost = hostCandidate.replace(/\.$/, '');
    if (singleDotHost === TIKTOK_HOST) {
      return firstPathSegment.replace(/^@+/, '');
    }
    if (singleDotHost.endsWith(TIKTOK_HOST_SUFFIX)) {
      const subdomain = singleDotHost.slice(0, -TIKTOK_HOST_SUFFIX.length);
      if (subdomain === 'www' || subdomain === 'm') {
        return firstPathSegment.replace(/^@+/, '');
      }
      return '';
    }
    return candidate.replace(/^@+/, '');
  }

  syncSubmit() {
    this.syncStep('waitlist', EMAIL_PATTERN.test(this.emailValue()));
    this.syncStep('q4', USERNAME_PATTERN.test(this.usernameValue()));
  }

  syncStep(screen, valid) {
    const active = this.screen === screen;
    const halted = this.halted.has(screen);
    this.cta(screen)?.setDisabled(
      halted || !valid || this.countryMissing || !active
    );
    const status = this.status(screen);
    if (!status || halted) return;
    const blocked = active && valid && this.countryMissing;
    if (blocked) status.textContent = this.dataset.submitError;
    status.hidden = !blocked;
  }

  status(screen) {
    return this.q(`[data-quiz-screen="${screen}"] [data-quiz-status]`);
  }

  async sendHandle() {
    const username = this.usernameValue();
    if (!USERNAME_PATTERN.test(username) || this.countryMissing) return;
    const messages = {
      error: this.dataset.sendError,
      unavailable: this.dataset.unavailableError
    };
    await this.send('q4', 'success', messages, {
      dest: 'tiktok',
      username,
      country: this.answers.countryIso,
      country_name: this.answers.countryName,
      california: this.answers.california ? 'Yes' : 'No'
    });
  }

  async sendEmail() {
    const email = this.emailValue();
    if (
      this.screen !== 'waitlist' ||
      !EMAIL_PATTERN.test(email) ||
      this.countryMissing
    ) {
      return;
    }
    const messages = {
      error: this.dataset.sendEmailError,
      unavailable: this.dataset.unavailableEmailError
    };
    await this.send('waitlist', 'waitlist-success', messages, {
      dest: 'waitlist',
      email,
      country: this.answers.countryIso,
      country_name: this.answers.countryName
    });
  }

  async send(screen, next, { error, unavailable }, fields) {
    if (this.sending || this.halted.has(screen)) return;
    if (!this.dataset.sheetEndpoint) {
      this.halted.add(screen);
      this.halt(screen, unavailable);
      this.track('signup', 'unavailable');
      return;
    }
    const key = JSON.stringify(fields);
    const attempt = this.attempts.get(screen);
    const submittedAt =
      attempt?.key === key ? attempt.submittedAt : new Date().toISOString();
    this.attempts.set(screen, { key, submittedAt });
    const status = this.status(screen);
    if (status) status.hidden = true;
    this.sending = true;
    this.cta(screen)?.setLoading(true);
    let sent = false;
    try {
      sent = await affiliateSignups.record(this.dataset.sheetEndpoint, {
        ...fields,
        submitted_at: submittedAt
      });
    } finally {
      this.sending = false;
      this.cta(screen)?.setLoading(false);
    }
    this.track('signup', sent ? 'success' : 'error');
    if (!sent) {
      if (status) {
        status.textContent = error;
        status.hidden = false;
      }
      return;
    }
    this.attempts.delete(screen);
    this.show(next);
  }

  halt(screen, message) {
    const status = this.status(screen);
    if (status) {
      status.textContent = message;
      status.hidden = false;
    }
    this.cta(screen)?.setLoading(false);
    this.cta(screen)?.setDisabled(true);
  }

  syncConsents() {
    const ready = Boolean(this.answers.terms && this.answers.privacy);
    this.cta('q2')?.setDisabled(!ready);
  }

  cta(screen) {
    return this.q(`[data-quiz-screen="${screen}"] .affiliate-quiz__cta-button`);
  }

  show(screen) {
    this.screen = screen;
    for (const panel of this.qa('[data-quiz-screen]')) {
      panel.hidden = panel.dataset.quizScreen !== screen;
    }
    for (const picture of this.qa('[data-quiz-picture]')) {
      picture.hidden = picture.dataset.quizPicture !== screen;
    }
    this.card.setAttribute('data-screen', screen);
    this.card.setAttribute('data-branch', this.branch);
    this.updateCounter();
    this.syncSubmit();
    this.q(`[data-quiz-screen="${screen}"] .affiliate-quiz__heading`)?.focus({
      preventScroll: true
    });
    this.track(
      'quiz_step',
      this.q(`[data-quiz-screen="${screen}"]`)?.dataset.analyticsStep ?? screen
    );
  }

  track(action, value) {
    bus.publish(ANALYTICS_TOPIC, {
      id: crypto.randomUUID(),
      event: ANALYTICS_EVENTS.stateChange,
      action,
      value,
      source: this
    });
  }

  updateCounter() {
    const counted = BRANCHES[this.branch] || [];
    const index = counted.indexOf(this.screen) + 1;
    if (!index) return;
    const value = this.counter?.getAttribute(
      `data-count-${this.branch}-${index}`
    );
    if (value) this.counter.textContent = value;
    this.card.setAttribute('data-step', String(index));
  }

  goBack() {
    if (this.sending) return;
    const counted = BRANCHES[this.branch] || [];
    const index = counted.indexOf(this.screen);
    if (index > 0) {
      this.show(counted[index - 1]);
      return;
    }
    this.leave();
  }

  leave() {
    const destination = this.resolveLanding();
    if (destination) location.assign(destination);
  }

  resolveLanding() {
    const configured = safeUrl(this.dataset.landingUrl);
    if (configured) return configured;
    const referrer = safeUrl(document.referrer);
    if (referrer && new URL(referrer).origin === location.origin) {
      return referrer;
    }
    return this.dataset.fallbackUrl || null;
  }

  isOldEnough(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    if (!year || !month || !day) return false;
    const eligible = Date.UTC(year + this.minAge, month - 1, day);
    const now = new Date();
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return eligible <= today;
  }

  focusable() {
    return this.qa(FOCUSABLE).filter(
      element =>
        !element.disabled &&
        element.getAttribute('tabindex') !== '-1' &&
        element.getClientRects().length > 0 &&
        getComputedStyle(element).visibility !== 'hidden'
    );
  }

  trapFocus(event) {
    const stops = this.focusable();
    if (!stops.length) return;
    const active = document.activeElement;
    const step = event.shiftKey ? -1 : 1;
    const current = active ? stops.indexOf(active) : -1;
    let index;
    if (current !== -1) {
      index = current + step;
    } else {
      const following = active
        ? stops.findIndex(
            stop =>
              active.compareDocumentPosition(stop) &
              Node.DOCUMENT_POSITION_FOLLOWING
          )
        : 0;
      index = following === -1 ? stops.length : following;
      if (step === -1) index -= 1;
    }
    event.preventDefault();
    stops[(index + stops.length) % stops.length].focus();
  }

  onKeydown(event) {
    if (event.defaultPrevented) return;
    if (event.key === 'Tab') {
      this.trapFocus(event);
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    if (this.select?.isOpen) {
      this.select.close({ restoreFocus: true });
      return;
    }
    if (this.dateField?.isOpen) {
      this.dateField.close({ restoreFocus: true });
      return;
    }
    this.leave();
  }

  resetOwnedControls() {
    if (this.screen !== 'q1' || this.answers.countryIso) return;
    for (const control of this.qa('input, select, textarea')) {
      if (control.type === 'checkbox' || control.type === 'radio') {
        control.checked = control.defaultChecked;
      } else if (control instanceof HTMLSelectElement) {
        for (const option of control.options) {
          option.selected = option.defaultSelected;
        }
      } else {
        control.value = control.defaultValue;
      }
    }
    for (const field of this.qa('text-field')) field.syncLabel?.();
  }

  onPageShow(event) {
    if (event.persisted) {
      location.reload();
      return;
    }
    this.resetOwnedControls();
  }
}

if (!customElements.get('affiliate-quiz')) {
  customElements.define('affiliate-quiz', AffiliateQuiz);
}
